import { getProfileImage } from '@/services/profileService'
import { RoomType } from '@/types/profile'

export type ProfileImageType = RoomType | 'matchmaking' | 'career'
type CanonicalProfileImageType = 'business' | 'matchmaking' | 'career'

interface CachedProfileImage {
  key: string
  userId: string
  eventType: CanonicalProfileImageType
  blob: Blob
  updatedAt: number
}

export class ProfileImageCacheService {
  private static readonly DB_NAME = 'comyuless'
  private static readonly DB_VERSION = 1
  private static readonly STORE_NAME = 'profile_images'

  private static normalizeType(type: ProfileImageType): CanonicalProfileImageType {
    if (type === 'dating') return 'matchmaking'
    if (type === 'job') return 'career'
    return type
  }

  private static makeKey(userId: string, type: CanonicalProfileImageType): string {
    return `${userId}:${type}`
  }

  private static openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(ProfileImageCacheService.DB_NAME, ProfileImageCacheService.DB_VERSION)

      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(ProfileImageCacheService.STORE_NAME)) {
          db.createObjectStore(ProfileImageCacheService.STORE_NAME, { keyPath: 'key' })
        }
      }

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  private static async getCachedBlob(userId: string, type: CanonicalProfileImageType): Promise<Blob | null> {
    const db = await ProfileImageCacheService.openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(ProfileImageCacheService.STORE_NAME, 'readonly')
      const store = tx.objectStore(ProfileImageCacheService.STORE_NAME)
      const request = store.get(ProfileImageCacheService.makeKey(userId, type))

      request.onsuccess = () => {
        const row = request.result as CachedProfileImage | undefined
        resolve(row?.blob ?? null)
      }
      request.onerror = () => reject(request.error)

      tx.oncomplete = () => db.close()
      tx.onerror = () => db.close()
      tx.onabort = () => db.close()
    })
  }

  private static async setCachedBlob(userId: string, type: CanonicalProfileImageType, blob: Blob): Promise<void> {
    const db = await ProfileImageCacheService.openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(ProfileImageCacheService.STORE_NAME, 'readwrite')
      const store = tx.objectStore(ProfileImageCacheService.STORE_NAME)
      const row: CachedProfileImage = {
        key: ProfileImageCacheService.makeKey(userId, type),
        userId,
        eventType: type,
        blob,
        updatedAt: Date.now(),
      }

      const request = store.put(row)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)

      tx.oncomplete = () => db.close()
      tx.onerror = () => db.close()
      tx.onabort = () => db.close()
    })
  }

  private static async fetchFromBackend(userId: string, requestedType: CanonicalProfileImageType): Promise<Blob | null> {
    const { imageBase64, contentType } = await getProfileImage(userId, requestedType)
    if (!imageBase64) return null

    const binary = atob(imageBase64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    const blob = new Blob([bytes], { type: contentType || 'image/jpeg' })
    await ProfileImageCacheService.setCachedBlob(userId, requestedType, blob)
    return blob
  }

  static async getImageBlob(userId: string, type: ProfileImageType): Promise<Blob | null> {
    const normalizedType = ProfileImageCacheService.normalizeType(type)

    try {
      const cached = await ProfileImageCacheService.getCachedBlob(userId, normalizedType)
      if (cached) return cached

      return await ProfileImageCacheService.fetchFromBackend(userId, normalizedType)
    } catch (error) {
      console.error('[ProfileImageCacheService] Error:', error)
      return null
    }
  }
}
